import React from 'react';
import { useUIStore } from '../stores/useUIStore';
import { X, ZoomIn, ZoomOut, RotateCcw, Download } from 'lucide-react';

export const MermaidLightboxModal: React.FC = () => {
  const { lightbox, closeLightbox, updateLightboxTransform, resetLightboxTransform } = useUIStore();

  if (!lightbox.isOpen || !lightbox.svgContent) return null;

  const handleZoomIn = () => {
    updateLightboxTransform(Math.min(lightbox.scale + 0.25, 4.0), lightbox.translateX, lightbox.translateY);
  };

  const handleZoomOut = () => {
    updateLightboxTransform(Math.max(lightbox.scale - 0.25, 0.5), lightbox.translateX, lightbox.translateY);
  };

  const handleDownloadSVG = () => {
    const blob = new Blob([lightbox.svgContent || ''], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${lightbox.diagramTitle?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'diagram'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xl flex items-center justify-center p-6">
      {/* Floating Control Glass Toolbar */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 glass-panel-heavy px-4 py-2 rounded-full border border-white/20 flex items-center space-x-4 shadow-2xl z-20 text-xs font-mono">
        <span className="text-white font-semibold truncate max-w-xs">{lightbox.diagramTitle}</span>

        <div className="h-4 w-px bg-slate-700" />

        <div className="flex items-center space-x-1">
          <button
            onClick={handleZoomOut}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-300 transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          <span className="px-2 text-cyan-400 font-bold min-w-[3rem] text-center">
            {Math.round(lightbox.scale * 100)}%
          </span>

          <button
            onClick={handleZoomIn}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-300 transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>

          <button
            onClick={resetLightboxTransform}
            className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-300 transition-colors"
            title="Reset 1:1"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="h-4 w-px bg-slate-700" />

        <button
          onClick={handleDownloadSVG}
          className="flex items-center space-x-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-md transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          <span>Export SVG</span>
        </button>

        <button
          onClick={closeLightbox}
          className="p-1.5 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded-lg transition-colors"
          title="Close Lightbox (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Interactive Pan/Zoom SVG Canvas Container */}
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing p-12"
        style={{
          transform: `scale(${lightbox.scale}) translate(${lightbox.translateX}px, ${lightbox.translateY}px)`,
          transition: 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        dangerouslySetInnerHTML={{ __html: lightbox.svgContent }}
      />
    </div>
  );
};
